import React, { useMemo, useEffect, useState } from 'react';
import {
  ChatBubbleLeftIcon,
  BanknotesIcon,
  UserGroupIcon,
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  BuildingOffice2Icon,
  IdentificationIcon as IdBadgeIcon,
  AcademicCapIcon,
  SparklesIcon,
  CpuChipIcon,
  DocumentPlusIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, ROLE_ADMIN, ROLE_COLLABORATOR_ADMIN, ROLE_COLLABORATOR_VIEWER, ROLE_COLLABORATOR_EDITOR } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components';
import { Collaborator } from '@/models';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared';
import { Link } from 'react-router-dom';
import { translateActivityStatus } from '@/utils/collaborator-status';
import { db } from '@/config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  buildExpensesView,
  useCollaboratorExpensesSummary,
} from '@/features/Collaborators/hooks/useCollaboratorExpensesSummary';
import { fetchCollaboratorSalary } from '@/features/Collaborators/Services/salarySubcollectionService';
import { useCollaboratorProfile } from '@/features/Collaborators/Data/hooks/useCollaboratorProfile';
import { useResponsibleEmailQuery } from '@/features/Collaborators/Data/hooks/useResponsibleEmailQuery';
import { useCollaboratorAllocationsQuery } from '@/features/Collaborators/Allocations/data';
import { useCollaboratorAllocationPreviewStatus } from '@/features/Collaborators/Allocations/hooks/useCollaboratorAllocationPreviewStatus';
import { ProfileAllocationsCard } from './components/ProfileAllocationsCard';
import { ProfileSkillsCard } from './components/ProfileSkillsCard';
import type { ResolvedResource } from '@/features/Collaborators/utils/resourcePresentation';

type CardConfig = {
  id: string;
  title: string;
  description?: string;
  icon: React.ElementType;
  colSpan?: string;
  content: React.ReactNode;
  extraHeader?: React.ReactNode;
};

const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="text-right text-gray-900">{value || 'Não informado'}</span>
  </div>
);

const formatPhone = (phone?: string | null) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

const buildWhatsappLink = (phone?: string | null) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10) return '';
  return `https://wa.me/${digits}`;
};

const formatCurrency = (value?: number | null) => {
  if (value === undefined || value === null) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (value?: any) => {
  if (!value) return '-';
  const seconds = value?.seconds ?? value?._seconds;
  if (seconds) {
    const date = new Date(seconds * 1000);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    if (typeof value === 'string' && value.trim()) return value;
    return '-';
  }
  return date.toLocaleDateString('pt-BR');
};

const formatElapsed = (start?: Date | null) => {
  if (!start || Number.isNaN(start.getTime())) return '-';
  const now = new Date();
  const totalMonths = Math.max(
    0,
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()),
  );
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (years > 0) {
    return `${years} ${years === 1 ? 'ano' : 'anos'}${months ? ` e ${months} ${months === 1 ? 'mês' : 'meses'}` : ''}`;
  }
  return `${months} ${months === 1 ? 'mês' : 'meses'}`;
};

const formatTenure = (start?: string | null, end?: string | null, isActive?: boolean) => {
  if (!start) return { prefix: 'Tempo de casa indisponível', value: '--' };
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : new Date();
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return { prefix: 'Tempo de casa indisponível', value: '--' };
  }
  const totalMonths = Math.max(
    0,
    (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth())
  );
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  const prefix = isActive ? 'Está conosco há' : 'Trabalhou na raro por';

  let value = '';
  if (years > 0 && months > 0) {
    value = `${years} ${years === 1 ? 'ano' : 'anos'} e ${months} ${months === 1 ? 'mês' : 'meses'}`;
  } else if (years > 0) {
    value = `${years} ${years === 1 ? 'ano' : 'anos'}`;
  } else {
    value = `${months} ${months === 1 ? 'mês' : 'meses'}`;
  }

  return { prefix, value: value || 'pouco tempo' };
};

const collaboratorTypeIcon = (type?: string | null) => {
  if (!type) return { icon: IdBadgeIcon, tone: 'from-slate-100 to-slate-200', label: 'Tipo não informado' };
  const normalized = type.toLowerCase();
  if (normalized.includes('fornecedor')) return { icon: BuildingOffice2Icon, tone: 'from-amber-100 to-amber-200', label: 'Fornecedor' };
  if (normalized.includes('sócio') || normalized.includes('socio')) return { icon: SparklesIcon, tone: 'from-fuchsia-100 to-pink-100', label: 'Sócio' };
  if (normalized.includes('estagi')) return { icon: AcademicCapIcon, tone: 'from-emerald-100 to-teal-100', label: 'Estagiário' };
  return { icon: BriefcaseIcon, tone: 'from-indigo-100 to-indigo-200', label: 'Celetista' };
};

const getStatusMeta = (status?: string | null) => {
  const label = translateActivityStatus(status);
  const normalized = status?.toLowerCase();
  if (normalized === 'active') return { label, tone: 'bg-emerald-50 text-emerald-700 ring-emerald-100' };
  if (normalized === 'inactive' || normalized === 'desligado') return { label, tone: 'bg-rose-50 text-rose-700 ring-rose-100' };
  if (normalized === 'afastado') return { label, tone: 'bg-amber-50 text-amber-700 ring-amber-100' };
  return { label, tone: 'bg-slate-50 text-slate-700 ring-slate-100' };
};

const getDepartmentLabel = (collaborator: Collaborator) => {
  const options = [
    collaborator.departmentName,
    (collaborator as any).department?.name,
    (collaborator as any).department?.title,
    collaborator.department,
  ];
  return options.find((item) => item && String(item).trim().length) || null;
};

function CollaboratorProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { collaborator, accessLevel, isLoading, error } = useCollaboratorProfile(id);
  const { userHasRole } = useAuth();
  const navigate = useNavigate();

  const [departmentLabel, setDepartmentLabel] = useState<string | null>(null);
  const [isDepartmentLoading, setIsDepartmentLoading] = useState(false);

  const { expensesSummary, isLoading: isExpensesLoading } = useCollaboratorExpensesSummary(collaborator);
  const [salaryData, setSalaryData] = useState<any | null>(null);
  const [isSalaryLoading, setIsSalaryLoading] = useState(false);
  const { data: responsibleEmail = null } = useResponsibleEmailQuery(collaborator?.id);

  const canSeeCards = accessLevel === 'full' || accessLevel === 'private' || accessLevel === 'self' || accessLevel === 'public';
  const canSeeSkills = accessLevel === 'full' || accessLevel === 'private';
  const canSeeSalary = accessLevel === 'full';
  const canSeeExpenses = userHasRole(ROLE_ADMIN);
  const canSeeAllocations = userHasRole(ROLE_ADMIN) || userHasRole(ROLE_COLLABORATOR_ADMIN) || userHasRole(ROLE_COLLABORATOR_VIEWER) || userHasRole(ROLE_COLLABORATOR_EDITOR);

  // Allocations Query
  const { data: allocations, isLoading: isAllocationsLoading } = useCollaboratorAllocationsQuery(canSeeAllocations ? collaborator?.id : null);
  const allocationStatus = useCollaboratorAllocationPreviewStatus(allocations || []);

  useEffect(() => {
    let cancelled = false;
    const label = collaborator ? getDepartmentLabel(collaborator) : null;
    if (label) {
      setDepartmentLabel(label);
      return;
    }

    const fetchDepartment = async () => {
      if (!collaborator?.departmentId) return;
      setIsDepartmentLoading(true);
      try {
        const snap = await getDoc(doc(db, 'departments', collaborator.departmentId));
        if (!snap.exists()) return;
        const data = snap.data() as { completeDescription?: string; name?: string; title?: string };
        const resolved = data.completeDescription || data.name || data.title || null;
        if (!cancelled) {
          setDepartmentLabel(resolved);
        }
      } catch (err) {
        console.error('[CollaboratorProfilePage] Failed to fetch department', err);
      } finally {
        if (!cancelled) {
          setIsDepartmentLoading(false);
        }
      }
    };

    fetchDepartment();
    return () => {
      cancelled = true;
    };
  }, [collaborator]);

  useEffect(() => {
    let cancelled = false;
    const fetchSalary = async () => {
      if (!collaborator?.id || !canSeeSalary) return;
      setIsSalaryLoading(true);
      try {
        const salary = await fetchCollaboratorSalary(collaborator.id);
        if (!cancelled) setSalaryData(salary);
      } catch (err) {
        console.error('[CollaboratorProfilePage] Failed to fetch salary', err);
      } finally {
        if (!cancelled) setIsSalaryLoading(false);
      }
    };
    fetchSalary();
    return () => {
      cancelled = true;
    };
  }, [collaborator?.id, canSeeSalary]);

  const mainAddress = collaborator?.addresses?.[0];
  const formattedAddressLine = useMemo(() => {
    if (!mainAddress) return '';
    const primaryParts = [mainAddress.street, mainAddress.number, mainAddress.complement]
      .filter(Boolean)
      .join(', ');
    const location = [mainAddress.city, mainAddress.state]
      .filter(Boolean)
      .join(' - ');
    const zip = mainAddress.zipCode;
    return [primaryParts, location, zip].filter(Boolean).join(' · ');
  }, [mainAddress]);

  const { expensesBenefitsSubtotal, expensesEncargosSubtotal, provisionsTotal, expensesTotal, hourlyCost } =
    buildExpensesView(expensesSummary);
  const expensesDetailPath = collaborator ? `/collaborators/${collaborator.id}/expenses` : null;
  const expensesSummaryItems = [
    { label: 'Benefícios', value: expensesBenefitsSubtotal },
    { label: 'Encargos', value: expensesEncargosSubtotal },
    { label: 'Provisões', value: provisionsTotal },
    { label: 'Total', value: expensesTotal },
    { label: 'Custo hora (168h)', value: hourlyCost },
  ];
  const hasExpensesData = expensesSummaryItems.some(
    (item) => item.value !== undefined && item.value !== null,
  );
  const currentSalary = (salaryData as any)?.currentEntry ?? null;
  const previousSalary = (salaryData as any)?.history?.[1] ?? null;
  const lastIncreaseDate = currentSalary?.changeDate ? new Date(currentSalary.changeDate) : null;
  const lastIncreaseDiff = lastIncreaseDate && !Number.isNaN(lastIncreaseDate.getTime()) ? lastIncreaseDate : null;
  const increasePercent =
    currentSalary?.amount && previousSalary?.amount
      ? ((currentSalary.amount - previousSalary.amount) / previousSalary.amount) * 100
      : null;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);
  const formatCompetence = (competence?: string | null) => {
    if (!competence) return 'Competência';
    const normalized = competence.replace(/\D/g, '');
    if (normalized.length >= 6) {
      const year = normalized.slice(0, 4);
      const month = normalized.slice(4, 6);
      const date = new Date(Number(year), Number(month) - 1, 1);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      }
    }
    return competence;
  };

  if (!canSeeCards) {
    return (
      <div className="rounded-xl border border-amber-100 bg-amber-50 p-6 text-amber-900">
        Você não tem permissão para visualizar os detalhes deste colaborador.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-red-800">
        Não foi possível carregar o colaborador.
      </div>
    );
  }

  if (!collaborator) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-gray-700 shadow-sm">
        Colaborador não encontrado.
      </div>
    );
  }

  const cards: CardConfig[] = [];

  // Contact Card
  cards.push({
    id: 'contact',
    title: 'Dados de contato',
    description: 'Canais oficiais',
    icon: ChatBubbleLeftIcon,
    colSpan: 'lg:col-span-1',
    extraHeader: collaborator ? (
      <Link
        to={`/collaborators/${collaborator.id}/basic-info`}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 transition hover:bg-indigo-100 hover:text-indigo-900"
        aria-label="Ver dados básicos"
        onClick={(e) => e.stopPropagation()}
      >
        <DocumentPlusIcon className="h-4 w-4" aria-hidden />
      </Link>
    ) : null,
    content: (
      <div className="space-y-4">
        <div>
          <p className="text-xs font-medium text-slate-500">E-mail Corporativo</p>
          {collaborator.email ? (
            <div className="mt-1 flex items-center justify-between">
              <a href={`mailto:${collaborator.email}`} className="text-sm font-medium text-indigo-600 hover:text-indigo-700 truncate mr-2">
                {collaborator.email}
              </a>
              <a
                href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(collaborator.email)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 rounded-full bg-indigo-50 p-1.5 text-indigo-600 transition hover:bg-indigo-100"
                title="Chat no Teams"
              >
                <ChatBubbleLeftIcon className="h-3.5 w-3.5" />
              </a>
            </div>
          ) : (
            <p className="mt-1 text-sm text-slate-400">Não informado</p>
          )}
        </div>

        <div>
          <p className="text-xs font-medium text-slate-500">Telefone</p>
          {collaborator.phone ? (
            <div className="mt-1 flex items-center justify-between">
              <a href={`tel:${collaborator.phone}`} className="text-sm font-medium text-slate-900 hover:text-slate-700">
                {formatPhone(collaborator.phone)}
              </a>
              {buildWhatsappLink(collaborator.phone) && (
                <a
                  href={buildWhatsappLink(collaborator.phone)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 rounded-full bg-emerald-50 p-1.5 text-emerald-600 transition hover:bg-emerald-100"
                  title="Conversar no WhatsApp"
                >
                  <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          ) : (
            <p className="mt-1 text-sm text-slate-400">Não informado</p>
          )}
        </div>

        <div>
          <p className="text-xs font-medium text-slate-500">Localização</p>
          <div className="mt-1 flex items-start gap-2">
            <MapPinIcon className="mt-0.5 h-4 w-4 text-slate-400 shrink-0" />
            <span className="text-sm text-slate-700">{formattedAddressLine || 'Endereço não cadastrado'}</span>
          </div>
        </div>
      </div>
    ),
  });

  // Context Card
  cards.push({
    id: 'context',
    title: 'Contexto',
    description: 'Responsável e Função',
    icon: UserGroupIcon,
    colSpan: 'lg:col-span-1',
    content: (
      <div className="space-y-4">
        <div>
          <p className="text-xs font-medium text-slate-500">Responsável</p>
          {collaborator.responsibleName ? (
            <div className="mt-2 flex items-center gap-3">
              <CollaboratorAvatar
                name={collaborator.responsibleName}
                size="small"
              />
              <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {collaborator.responsibleName}
                  </p>
                  {responsibleEmail && (
                    <p className="text-xs text-slate-500 truncate" title={responsibleEmail}>
                      {responsibleEmail}
                    </p>
                  )}
                </div>

                {collaborator.responsibleId && (
                  <Link
                    to={`/collaborators/${collaborator.responsibleId}`}
                    className="text-xs text-indigo-600 hover:text-indigo-700 whitespace-nowrap"
                  >
                    Ver perfil
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <p className="mt-1 text-sm text-slate-400">Não definido</p>
          )}
        </div>

        {collaborator.roleType && (
          <div>
            <p className="text-xs font-medium text-slate-500">Tipo de Função</p>
            <p className="mt-1 text-sm text-slate-900">
              {collaborator.roleType === 'administrative' ? 'Administrativo' : 'Operacional'}
            </p>
          </div>
        )}
      </div>
    ),
  });

  // Role Card (Full/Private/Self only)
  if (accessLevel !== 'public') {
    cards.push({
      id: 'role',
      title: 'Contratação',
      description: 'Vínculo, liderança e alocação interna',
      icon: BriefcaseIcon,
      colSpan: 'lg:col-span-2',
      content: (
        (() => {
          const status = collaborator.status?.toLowerCase();
          const isActive = !collaborator.endDate && status !== 'inactive';
          const tenure = formatTenure(collaborator.startDate, collaborator.endDate, isActive);
          const typeMeta = collaboratorTypeIcon(collaborator.collaboratorType);
          const statusMeta = getStatusMeta(collaborator.status);

          const roleTypeLabel =
            collaborator.roleType === 'administrative'
              ? 'Função administrativa'
              : collaborator.roleType === 'operational'
                ? 'Função operacional'
                : 'Função não informada';

          const workPrefs =
            collaborator.preferredWorkModels && collaborator.preferredWorkModels.length
              ? collaborator.preferredWorkModels
              : ['Preferência não informada'];

          return (
            <div className="flex h-full flex-col gap-4">
              <div className="flex w-full flex-nowrap items-stretch gap-3 overflow-x-auto pb-1">
                {/* Desenvolvedor / Cargo */}
                <div className="flex flex-1 basis-auto shrink whitespace-nowrap items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                  <div className="flex flex-col">
                    <p className="text-sm font-semibold text-slate-900">
                      {collaborator.positionName || 'Não informado'}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {collaborator.seniorityName ? (
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-indigo-700 shadow-sm">
                          {collaborator.seniorityName}
                        </span>
                      ) : null}
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                        {roleTypeLabel}
                      </span>
                      {collaborator.workSchedule ? (
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-700 shadow-sm">
                          {collaborator.workSchedule}h/semana
                        </span>
                      ) : null}
                      {workPrefs.map((pref) => (
                        <span
                          key={`pref-badge-${pref}`}
                          className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm"
                        >
                          {pref}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Tempo */}
                <div className="flex flex-1 basis-auto shrink whitespace-nowrap flex-col justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-700 px-4 py-3 text-white shadow-lg shadow-indigo-200/60">
                  <span className="text-xs font-semibold uppercase tracking-wide text-indigo-100">
                    {tenure.prefix}
                  </span>
                  <span className="break-words text-xl font-semibold leading-tight md:text-2xl md:leading-tight">
                    {tenure.value}
                  </span>
                </div>

                {/* Tipo */}
                <div className="flex flex-1 basis-auto shrink whitespace-nowrap items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                  <div className="flex flex-1 flex-col gap-1">
                    <span className="text-sm font-semibold text-slate-900">
                      {collaborator.collaboratorType || typeMeta.label}
                    </span>
                    <span
                      className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ring-1 ${statusMeta.tone}`}
                    >
                      <span className="h-2 w-2 rounded-full bg-current" />
                      {statusMeta.label}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()
      ),
      extraHeader: collaborator ? (
        <Link
          to={`/collaborators/${collaborator.id}/contract`}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 transition hover:bg-indigo-100 hover:text-indigo-900"
          aria-label="Ver detalhes de contratação"
          onClick={(e) => e.stopPropagation()}
        >
          <DocumentPlusIcon className="h-4 w-4" aria-hidden />
        </Link>
      ) : null,
    });
  }

  if (canSeeSkills) {
    cards.push({
      id: 'skills',
      title: 'Habilidades',
      description: 'Capacidade principal, tecnologias e senioridades',
      icon: CpuChipIcon,
      colSpan: 'lg:col-span-2',
      content: (
        <ProfileSkillsCard resources={(collaborator.resources ?? []) as ResolvedResource[]} />
      ),
    });
  }

  // Allocation card is rendered directly (not via cards array) — see JSX below

  if (canSeeSalary) {
    cards.push({
      id: 'salary',
      title: 'Remuneração',
      description: 'Dados salariais atuais',
      icon: BanknotesIcon,
      colSpan: 'lg:col-span-2',
      extraHeader: collaborator ? (
        <Link
          to={`/collaborators/${collaborator.id}/salary`}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 transition hover:bg-indigo-100 hover:text-indigo-900"
          aria-label="Ver detalhamento salarial"
          onClick={(e) => e.stopPropagation()}
        >
          <DocumentPlusIcon className="h-4 w-4" aria-hidden />
        </Link>
      ) : null,
      content: (
        <div className="space-y-3">
          {isSalaryLoading ? <p className="text-xs text-slate-500">Carregando salário...</p> : null}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tipo</p>
              <p className="text-sm font-semibold text-slate-900">
                {currentSalary?.salaryType ?? currentSalary?.currentSalaryType ?? '-'}
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Salário</p>
              <p className="text-xl font-semibold text-slate-900">
                {formatCurrency(currentSalary?.amount)}
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Extra (Caju)</p>
              <p className="text-xl font-semibold text-slate-900">
                {formatCurrency(currentSalary?.extras)}
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Data do último salário</p>
              <p className="text-sm font-semibold text-slate-900">
                {formatDate(currentSalary?.changeDate)}
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">% do último aumento</p>
              <p className="text-xl font-semibold text-slate-900">
                {increasePercent === null ? '-' : `${increasePercent.toFixed(1)}%`}
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tempo sem aumento</p>
              <p className="text-sm font-semibold text-slate-900">
                {lastIncreaseDiff === null ? '-' : formatElapsed(lastIncreaseDiff)}
              </p>
            </div>
          </div>
        </div>
      ),
    });
  }

  if (canSeeExpenses) {
    cards.push({
      id: 'expenses-row',
      title: 'Despesas',
      description: 'Salário, benefícios e totais',
      icon: BanknotesIcon,
      colSpan: 'lg:col-span-2',
      content: (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {expensesSummaryItems.map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.label}</p>
                <p className="text-xl font-semibold text-slate-900">{formatCurrency(item.value)}</p>
              </div>
            ))}
          </div>
          {!hasExpensesData && !isExpensesLoading ? (
            <p className="text-sm text-slate-500">Sem dados consolidados para esta competência.</p>
          ) : null}
        </div>
      ),
      extraHeader: (
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Competência</p>
            <p className="text-sm font-semibold text-slate-800">
              {formatCompetence(expensesSummary?.competence)}
            </p>
            {isExpensesLoading ? (
              <p className="text-xs text-slate-500">Carregando...</p>
            ) : null}
          </div>
          {expensesDetailPath ? (
            <Link
              to={expensesDetailPath}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 transition hover:bg-indigo-100 hover:text-indigo-900"
              aria-label="Ver detalhamento de despesas"
            >
              <DocumentPlusIcon className="h-4 w-4" aria-hidden />
            </Link>
          ) : null}
        </div>
      ),
    });
  }

  return (
    <div className="w-full space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column: Contact & Context */}
        <div className="space-y-6 lg:col-span-1">
          {cards.filter(c => c.id === 'contact' || c.id === 'context').map(card => (
            <section
              key={card.id}
              className="group relative flex flex-col rounded-2xl border border-slate-100 bg-white p-5 shadow-sm ring-1 ring-slate-100 transition duration-300 hover:shadow-md"
            >
              <div className="relative flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <card.icon className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-900">
                      {card.title}
                    </p>
                    {card.description ? (
                      <p className="text-xs text-slate-500">{card.description}</p>
                    ) : null}
                  </div>
                </div>
                {card.extraHeader}
              </div>
              <div className="relative flex-1">{card.content}</div>
            </section>
          ))}
        </div>

        {/* Right Column: Alocação, Remuneração, Contratação, Despesas */}
        <div className="space-y-6 lg:col-span-2">
          {/* Allocations Card */}
          {canSeeAllocations && collaborator && (
            <ProfileAllocationsCard
              collaboratorId={collaborator.id!}
              allocations={allocations || []}
              allocationStatus={allocationStatus}
              isLoading={isAllocationsLoading}
            />
          )}

          {/* Remaining cards in order: skills, salary, role, expenses-row */}
          {(['skills', 'salary', 'role', 'expenses-row'] as const)
            .map(id => cards.find(c => c.id === id))
            .filter((card): card is CardConfig => !!card)
            .map(card => (
            <section
              key={card.id}
              className="group relative flex flex-col rounded-2xl border border-slate-100 bg-white p-5 shadow-sm ring-1 ring-slate-100 transition duration-300 hover:shadow-md"
            >
              <div className="relative flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <card.icon className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-900">
                      {card.title}
                    </p>
                    {card.description ? (
                      <p className="text-xs text-slate-500">{card.description}</p>
                    ) : null}
                  </div>
                </div>
                {card.extraHeader}
              </div>
              <div className="relative flex-1">{card.content}</div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CollaboratorProfilePage;
